import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { getSessionsList } from '../endpoints/sessions/list_GET.schema';
import { createSession } from '../endpoints/sessions/create_POST.schema';
import { getSession } from '../endpoints/sessions/get_GET.schema';
import { updateSession } from '../endpoints/sessions/update_POST.schema';
import { deleteSession } from '../endpoints/sessions/delete_POST.schema';
import { SessionStatus } from './schema';

export const useSessionsList = (status?: SessionStatus) => {
  return useQuery({
    queryKey: ['sessions', 'list', status],
    queryFn: () => getSessionsList({ status }),
    // Avoid aggressive retries/refetches while debugging 404 routing issues
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
};

export const useSession = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['sessions', 'detail', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getSession({ id: sessionId });
    },
    enabled: !!sessionId,
    // Avoid aggressive retries/refetches when the API returns 404
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
};

export const useCreateSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] });
    },
  });
};

export const useUpdateSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSession,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', data.session.id] });
    },
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSession,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sessions', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', variables.id] });
    },
  });
};
